"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.FINANCIAL_RECEIPT_CORRECTION_REQUEST_EXAMPLES = exports.receiptCorrectionStatusEnum = exports.listReceiptCorrectionsQuerySchema = exports.rejectReceiptCorrectionSchema = exports.approveReceiptCorrectionSchema = exports.requestReceiptCorrectionSchema = exports.previewReceiptCorrectionSchema = exports.receiptCorrectionKindEnum = void 0;
/**
 * Request contracts for RECEIPT CORRECTIONS — fixing WHO or WHICH STAY a
 * receipt belongs to, as opposed to `financial-payment-correction.request.ts`,
 * which fixes HOW it was paid (mode / bank / reference).
 *
 * Desk, 2026-10-05: "for OP receipt pertaining to OP number and IP advance is
 * Bill number", and "need doctor transfer option also when appointment CONS FEE
 * taken". So there are three kinds:
 *
 *   OP_NUMBER              an OP receipt (a `FinInvoice`) was raised under the
 *                          wrong patient. OP number = the patient's UHID.
 *   DOCTOR_TRANSFER        a consultation (CONS FEE) was taken for the wrong
 *                          doctor — move the appointment AND the bill to another
 *                          doctor, before the consultation starts.
 *   IP_ADVANCE_BILL_NUMBER an IP advance receipt (an `IpAdmissionDeposit`)
 *                          was taken against the wrong stay. Bill number = the
 *                          stay's `IPB…` number, one per admission.
 *
 * Same discipline as the payment-mode flow: a request only ever creates a
 * PENDING row; nothing moves until a SuperAdmin approves (`approve` re-checks
 * every rule inside its own transaction). The two flows share the status enum
 * and the approve / reject / list bodies on purpose — one queue, one vocabulary.
 *
 * The identifier the desk TYPES is the number they can read off the paper
 * (`requestedOpNumber`, `requestedBillNumber`); the server resolves it to a
 * patient / stay. A uuid typed by a human is not a thing.
 */
const zod_1 = require("zod");
const financial_payment_correction_request_1 = require("./financial-payment-correction.request");
exports.receiptCorrectionKindEnum = zod_1.z.enum(['OP_NUMBER', 'DOCTOR_TRANSFER', 'IP_ADVANCE_BILL_NUMBER']);
const opNumberTargetSchema = zod_1.z.object({
    kind: zod_1.z.literal('OP_NUMBER'),
    /** The OP receipt — `FinInvoice.id`. */
    invoiceId: zod_1.z.string().uuid(),
    /** The patient the receipt SHOULD be under, by UHID. Matched exactly, case-insensitively. */
    requestedOpNumber: zod_1.z.string().trim().min(1, 'Enter the correct OP number').max(30),
});
const doctorTransferTargetSchema = zod_1.z.object({
    kind: zod_1.z.literal('DOCTOR_TRANSFER'),
    /** The OP receipt that carries the consultation — `FinInvoice.id`. */
    invoiceId: zod_1.z.string().uuid(),
    /** The doctor it should move to, as a USER id (`Appointment.doctorId` is a User FK) — what `DoctorDto.userId` is. */
    requestedDoctorId: zod_1.z.string().uuid(),
});
const advanceBillNumberTargetSchema = zod_1.z.object({
    kind: zod_1.z.literal('IP_ADVANCE_BILL_NUMBER'),
    /** The advance receipt — `IpAdmissionDeposit.id` (what the Bill Register carries as the row id of an advance). */
    depositId: zod_1.z.string().uuid(),
    /** The stay the advance SHOULD be against, by its Bill number. Matched exactly, case-insensitively. */
    requestedBillNumber: zod_1.z.string().trim().min(1, 'Enter the correct Bill number').max(30),
});
const reasonSchema = zod_1.z.string().trim().min(3, 'A reason is required').max(2000);
/**
 * "Would this be allowed, and what would it do?" — asked BEFORE the clerk writes
 * a reason, so a receipt that can never be moved says so up front. The request
 * endpoint runs the very same assessment, so the screen's answer and the
 * server's refusal cannot disagree.
 */
exports.previewReceiptCorrectionSchema = zod_1.z.discriminatedUnion('kind', [opNumberTargetSchema, doctorTransferTargetSchema, advanceBillNumberTargetSchema]);
exports.requestReceiptCorrectionSchema = zod_1.z.discriminatedUnion('kind', [
    opNumberTargetSchema.extend({ reason: reasonSchema }),
    doctorTransferTargetSchema.extend({ reason: reasonSchema }),
    advanceBillNumberTargetSchema.extend({ reason: reasonSchema }),
]);
/** Decisions use the payment-correction bodies unchanged — see the header. */
exports.approveReceiptCorrectionSchema = financial_payment_correction_request_1.approvePaymentCorrectionSchema;
exports.rejectReceiptCorrectionSchema = financial_payment_correction_request_1.rejectPaymentCorrectionSchema;
exports.listReceiptCorrectionsQuerySchema = financial_payment_correction_request_1.listPaymentCorrectionsQuerySchema;
exports.receiptCorrectionStatusEnum = financial_payment_correction_request_1.paymentCorrectionStatusEnum;
exports.FINANCIAL_RECEIPT_CORRECTION_REQUEST_EXAMPLES = {
    opNumber: {
        kind: 'OP_NUMBER',
        invoiceId: '11111111-1111-1111-1111-111111111111',
        requestedOpNumber: '00044M',
        reason: 'Receipt raised under the wrong UHID — the patient who paid is 00044M.',
    },
    doctorTransfer: {
        kind: 'DOCTOR_TRANSFER',
        invoiceId: '11111111-1111-1111-1111-111111111111',
        requestedDoctorId: '44444444-4444-4444-4444-444444444444',
        reason: 'Patient wants to see the other doctor in the same department — consultation already paid.',
    },
    advanceBillNumber: {
        kind: 'IP_ADVANCE_BILL_NUMBER',
        depositId: '22222222-2222-2222-2222-222222222222',
        requestedBillNumber: 'IPB2026000069',
        reason: 'Advance was entered against the previous stay — it belongs to bill IPB2026000069.',
    },
};
//# sourceMappingURL=financial-receipt-correction.request.js.map